import {Box, Typography} from "@material-ui/core";
import messagingService from "api/messaging";
import {useConversation, useCurrentShow} from "hooks";
import {getReadableString} from "constraints/general";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {format, isToday, isYesterday, startOfDay} from "date-fns";
import FlexBox from "view/components/Layout/FlexBox";
import {Loop} from "@material-ui/icons";
import {
  ConversationData,
  Message,
  StandsWithCompanies,
  UploadFile,
  UserInfo,
} from "ts/api/chat";
import NoMessageIcon from "view/images/NoMessageIcon";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import {Timestamp} from "api/firebase";
import useExhibitorPairsByStandIds from "hooks/exhibitorPair/useExhibitorPairsByStandIds";
import groupPairsByStand, {findStandLogo} from "tools/chat/groupPairsByStand";
import {Roles} from "@evennt-tech/api-v2-core";
import Bubble from "./components/Bubble";
import SendMessage from "./components/SendMessage";
import Search from "./components/Search";
import Avatar from "../Avatar";
import {useStyles} from "./style";

import DeleteMessageModal from "./components/DeleteMessageModal";

type ConversationProps = {
  showId: string;
  userId: string;
  userRole: string;
  standId: string;
  exhibitorPairId?: string;
  localizedExhibitorLabel: string;
  organiserId: string;
  fullName?: string;
  contractorCompanyName?: string;
  selectedConversation: string;
  createConversation?: (data: StandsWithCompanies) => void;
  conversationData?: ConversationData;
  hasConversations?: boolean;
  creatingNewChat?: boolean;
  conversationWasChanged?: boolean;
  localizedOrganiserLabel: string;
};
const Conversation = ({
  localizedExhibitorLabel,
  showId,
  userId,
  userRole,
  standId,
  organiserId,
  exhibitorPairId,
  fullName,
  contractorCompanyName = "",
  selectedConversation = "",
  createConversation,
  conversationData,
  hasConversations,
  creatingNewChat,
  conversationWasChanged,
  localizedOrganiserLabel,
}: ConversationProps) => {
  const classes = useStyles();
  const conversationId = `${selectedConversation}-organisers`;
  const [show] = useCurrentShow();
  const dateFormat = getReadableString(show);
  const {
    messages,
    loading,
    error,
    loadOlderMessages,
    loadAllMessagesToId,
  } = useConversation(showId, conversationId);

  const conversationStandIds = useMemo(
    () => (selectedConversation ? [selectedConversation] : []),
    [selectedConversation]
  );
  const [pairs] = useExhibitorPairsByStandIds({
    showId,
    legacyStandIds: conversationStandIds,
  });

  const [editText, setEditText] = useState("");
  const [asyncScrollToReplay, setAsyncScrollToReplay] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [allStandsName, setAllStandsName] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [deleteType, setDeleteType] = useState("");
  const [deleteFileId, setDeleteFileId] = useState("");
  const [deleteFileName, setDeleteFileName] = useState("");
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(
    true
  );
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [replayText, setReplayText] = useState("");
  const [replayId, setReplayId] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map()).current;
  const [recalculating, setRecalculating] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [currentStand, setCurrentStand] = useState<StandsWithCompanies | null>(
    null
  );

  const standsWithCompanies = useMemo(() => groupPairsByStand(pairs), [pairs]);

  const findFirstUnreadMessageId = useCallback(
    (userId: string, messageList: Message[]) => {
      const unreadMessage = messageList?.find(
        (message: Message) =>
          !message.read && message.senderInfo.senderId !== userId
      );
      return unreadMessage ? unreadMessage.id : null;
    },
    []
  );

  useEffect(() => {
    if (standsWithCompanies.length && selectedConversation) {
      setShowScrollToBottomButton(true);
      const item = standsWithCompanies.find(
        (currStand) => currStand.stand.standId === selectedConversation
      );
      if (item) {
        setCurrentStand(item);
        setAvatarUrl(findStandLogo(item.pairs));
      }
    }
  }, [standsWithCompanies, selectedConversation]);

  const recalculateUnread = useCallback(() => {
    if (selectedConversation && messages?.length) {
      const unreadMessages = messages.filter((message) => !message.read);
      const standUnread = unreadMessages?.filter(
        (message) => message.senderInfo.role === "organiser"
      );

      const organiserUnread = unreadMessages?.filter(
        (message) => message.senderInfo.role !== "organiser"
      );
      messagingService.refreshUnread(
        showId,
        standId,
        organiserUnread?.length || 0,
        standUnread?.length || 0
      );
    }
  }, [messages, selectedConversation, showId, standId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute("data-message-id");
            if (messageId) {
              messagingService.updateMessageReadStatus(
                showId,
                standId,
                messageId,
                userRole === "organiser"
              );
            }
          }
        });
      },
      {threshold: 0.1}
    );

    messageRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [messages, selectedConversation, messageRefs, standId, showId, userRole]);

  useEffect(() => {
    if (
      conversationWasChanged &&
      !loading &&
      !recalculating &&
      messages?.length > 5
    ) {
      setRecalculating(true);
      const firstUnreadMessageId = findFirstUnreadMessageId(userId, messages);
      const hasUnread =
        firstUnreadMessageId && messageRefs.has(firstUnreadMessageId);

      if (hasUnread) {
        const firstUnreadMessageRef = messageRefs.get(firstUnreadMessageId);
        setTimeout(
          () =>
            firstUnreadMessageRef.current?.scrollIntoView({
              behavior: "auto",
            }),
          300
        );
      } else {
        setTimeout(
          () =>
            messagesEndRef.current?.scrollIntoView({
              behavior: "auto",
            }),
          300
        );
      }
      setTimeout(() => setRecalculating(false), 500);
    }
  }, [
    messages,
    conversationWasChanged,
    loading,
    userId,
    findFirstUnreadMessageId,
    messageRefs,
    selectedConversation,
    recalculateUnread,
    recalculating,
    setRecalculating,
    setShowScrollToBottomButton,
  ]);

  const formatDate = (timestamp: Timestamp) => {
    const date = new Date(timestamp.seconds * 1000);
    if (isToday(date)) {
      return "Today";
    }
    if (isYesterday(date)) {
      return "Yesterday";
    }
    return format(date, dateFormat);
  };

  const startsNewDay = (current: Timestamp, previous: Timestamp) => {
    if (!previous) {
      return true;
    }

    const currentDate = startOfDay(new Date(current.seconds * 1000));
    const previousDate = startOfDay(new Date(previous.seconds * 1000));

    return currentDate > previousDate;
  };

  const handleLazyLoad = async () => {
    if (messages.length > 0) {
      const messageToMaintainViewOnId = messages[0].id;
      await loadOlderMessages();
      const ref = messageRefs.get(messageToMaintainViewOnId);

      if (ref && ref.current) {
        ref.current.scrollIntoView();
      }
    }
  };

  const handleScroll = () => {
    if (
      messagesContainerRef?.current?.scrollTop === 0 &&
      !conversationWasChanged
    ) {
      handleLazyLoad();
    }
  };

  const handleScrollButton = useCallback(() => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isOutOfView =
        container.scrollHeight - 50 >
        container.clientHeight + container.scrollTop;

      setShowScrollToBottomButton(isOutOfView);
    }
  }, [messagesEndRef, messagesContainerRef]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    container?.addEventListener("scroll", handleScrollButton);

    return () => container?.removeEventListener("scroll", handleScrollButton);
  }, [handleScrollButton, conversationId]);

  const send = async (message: string, files?: UploadFile[]) => {
    if (!currentStand) {
      return;
    }
    if (editId) {
      await messagingService.editStandToOrganisersMessage(
        showId,
        standId,
        editId,
        message,
        files
      );
      setEditText("");
      setEditId("");
    } else {
      let newInfo = null;
      if (!conversationData?.userInfo) {
        const item = standsWithCompanies.find(
          (item) => item.stand.standId === standId
        );
        if (item) {
          newInfo = {
            id: standId,
            name: `(${item.stand.name}) ${item.allCompaniesNames}`,
            logo: findStandLogo(item.pairs),
          };
        }
      }
      const userInfo: UserInfo = conversationData?.userInfo
        ? {...conversationData.userInfo}
        : (newInfo as UserInfo);
      const subName =
        userRole === "organiser"
          ? "Organiser"
          : `${localizedExhibitorLabel}, ${userInfo?.name}`;

      await messagingService.sendStandToOrganisersMessage(
        showId,
        currentStand,
        {
          ...userInfo,
          role: userRole,
          fullName,
          contractorCompanyName,
          subName,
          senderId: userId,
          replayId: replayId || "",
          replayText: replayText || "",
        },
        userRole === "organiser" ? "organiser" : standId,
        message,
        files || [],
        false
      );
      setReplayText("");
      setReplayId("");
      recalculateUnread();
    }
    setTimeout(() => {
      messagesEndRef?.current?.scrollIntoView({behavior: "smooth"});
    }, 100);
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({behavior: "smooth"});
  };

  const editMessage = (text: string, id: string) => {
    setEditText(text);
    setEditId(id);
  };

  const deleteMessage = (
    id: string,
    type: string,
    fileId?: string,
    fileName?: string
  ) => {
    setDeleteId(id);
    setDeleteType(type);
    if (fileId && fileName) {
      setDeleteFileId(fileId);
      setDeleteFileName(fileName);
    }
    setDeleteIsOpen(true);
  };

  const replayMessage = (text: string, id: string) => {
    setReplayText(text);
    setReplayId(id);
    const input: any = document.querySelector("#chat-text-input");
    if (input) {
      input.focus();
    }
  };

  const goToReplayRef = async (id: string) => {
    let messageRef = messageRefs.get(id);
    if (messageRef && messageRef.current) {
      messageRef.current.scrollIntoView({behavior: "smooth"});
    } else {
      await loadAllMessagesToId(id);
      setAsyncScrollToReplay(id);
    }
  };

  const deleteFile = async (
    fileId: string,
    fileName: string,
    messageId: string
  ) => {
    await messagingService.deleteFileMessage(
      showId,
      standId,
      messageId,
      fileId,
      fileName
    );
  };

  const onModalDeleteConfirm = () => {
    if (deleteId && deleteType === "message") {
      messagingService.softDeleteStandToOrganisersMessage(
        showId,
        standId,
        deleteId
      );
    }
    if (deleteId && deleteFileId && deleteType === "file") {
      deleteFile(deleteFileId, deleteFileName, deleteId);
    }

    setDeleteIsOpen(false);
    setDeleteId("");
    setDeleteFileId("");
    setDeleteType("");
    setDeleteFileName("");
  };

  const onModalDeleteClose = () => {
    setDeleteIsOpen(false);
    setDeleteId("");
  };

  const onReplayEditClose = () => {
    setEditId("");
    setEditText("");
    setReplayId("");
    setReplayText("");
  };

  const getBubbles = () => {
    if (loading) {
      return null;
    }

    if (messages?.length) {
      let previousMessage: Message | null = null;

      const bubbles = messages.map((message: Message, index: number) => {
        const isNewDay = startsNewDay(
          message.date,
          previousMessage?.date as Timestamp
        );
        previousMessage = message;
        const {date} = message;
        if (!messageRefs.has(message.id)) {
          messageRefs.set(message.id, React.createRef());
        }

        const messageRef = messageRefs.get(message.id);
        const time =
          date && date.seconds ? format(date.seconds * 1000, "HH:mm") : "";
        const isSender = userId === message.senderInfo.senderId;
        const img =
          message.senderInfo.role === "organiser"
            ? show?.logoFile?.url
            : avatarUrl || message.senderInfo?.logo || "";
        const exhibitorId =
          message.senderInfo.role === "exhibitor"
            ? message.senderInfo.senderId
            : "";

        const unread = !message.read && userId !== message.senderInfo.senderId;

        const subName =
          userRole === Roles.EXHIBITOR &&
          message.senderInfo.role === Roles.ORGANISER
            ? (localizedOrganiserLabel as string)
            : message.senderInfo.subName;
        return (
          <React.Fragment key={message.id}>
            {isNewDay && (
              <Box
                margin={index === 0 ? "50px 0 12px" : "12px 0"}
                className={classes.dateDivider}
              >
                {formatDate(date)}
              </Box>
            )}
            <div ref={messageRef} />
            <Bubble
              showId={showId}
              standId={standId}
              exhibitorPairId={exhibitorPairId}
              exhibitorId={exhibitorId}
              id={message.id}
              index={index}
              isSender={isSender}
              fullName={message.senderInfo.fullName}
              contractorCompanyName={message.senderInfo.contractorCompanyName}
              text={message.text}
              subName={subName}
              time={time}
              read={message.read}
              isDeleted={message.isDeleted}
              isEdited={message.isEdited}
              onEdit={editMessage}
              onDelete={deleteMessage}
              onReplay={replayMessage}
              replayId={message.senderInfo.replayId}
              replayText={message.senderInfo.replayText}
              goToReplayRef={goToReplayRef}
              files={message.files}
              userRole={message.senderInfo.role}
              currentRole={userRole}
              img={img}
            />
            {unread && (
              <div
                style={{width: "100%", height: "1px"}}
                ref={messageRef}
                data-message-id={message.id}
              />
            )}
          </React.Fragment>
        );
      });

      if (asyncScrollToReplay) {
        setTimeout(() => {
          const messageRef = messageRefs.get(asyncScrollToReplay);
          if (messageRef && messageRef.current) {
            messageRef.current.scrollIntoView({behavior: "smooth"});
            setAsyncScrollToReplay("");
          }
        }, 300);
      }

      return bubbles;
    }

    return null;
  };

  const newMessage =
    !creatingNewChat && conversationData && userRole === "organiser";
  const createNewConversation = !!creatingNewChat && !!createConversation;
  const getConversation = () => {
    if (!selectedConversation && !createNewConversation) {
      return (
        <FlexBox
          alignCenter
          justifyCenter
          className={classes.conversationContainer}
          position="relative"
        >
          <NoMessageIcon />
          {!hasConversations && (
            <Box fontSize="24px" fontWeight="600">
              No Messages, yet
            </Box>
          )}
          {!hasConversations && (
            <Box maxWidth="270px" fontSize="18px" textAlign="center">
              Pick an exhibitor from left menu and start conversation
            </Box>
          )}
          {hasConversations && (
            <Box maxWidth="270px" fontSize="18px" textAlign="center">
              Select a chat to start messaging
            </Box>
          )}
        </FlexBox>
      );
    }

    return (
      <Box className={classes.conversationWrapper}>
        {createNewConversation && (
          <Search createConversation={createConversation} />
        )}
        {newMessage && (
          <FlexBox
            flexDirection="row"
            borderBottom="1px solid rgba(0, 0, 0, 0.15)"
            padding="24px"
          >
            <Avatar
              img={avatarUrl || conversationData?.userInfo?.logo || ""}
              name={conversationData?.userInfo?.name || ""}
            />
            <Box marginLeft="16px">
              <Typography variant="h3">
                {conversationData?.userInfo?.name || ""}{" "}
              </Typography>
              <Typography className={classes.subName}>
                {localizedExhibitorLabel}
              </Typography>
            </Box>
          </FlexBox>
        )}
        <div
          className={classes.bubbleContainer}
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {selectedConversation && getBubbles()}
          <div ref={messagesEndRef} />
          {(asyncScrollToReplay || showScrollToBottomButton) && (
            <Box
              bottom={userRole === "organiser" ? 110 : 100}
              right={userRole === "organiser" ? 60 : 10}
              className={classes.scrollToBottomButton}
              onClick={scrollToBottom}
            >
              {asyncScrollToReplay && (
                <Loop
                  style={{
                    color: "white",
                    animation: "spin 1s linear infinite",
                  }}
                />
              )}
              {showScrollToBottomButton && !asyncScrollToReplay && (
                <KeyboardArrowDown fontSize="small" />
              )}
            </Box>
          )}
        </div>

        {selectedConversation && (
          <Box padding="8px" marginBottom="20px">
            <SendMessage
              showId={showId}
              standId={standId}
              editText={editText}
              replayText={replayText}
              onSend={send}
              onReplayEditClose={onReplayEditClose}
              userRole={userRole}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.conversationWrapper}>
      {getConversation()}
      <DeleteMessageModal
        open={deleteIsOpen}
        handleClose={onModalDeleteClose}
        handleSubmit={onModalDeleteConfirm}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Conversation;
